import React from 'react';
import { TextColorOption } from '../../theming';
import { BreadcrumbItem, BreadcrumbItemProps } from './BreadcrumbItem';
import { Scroller, ScrollerProps } from '../Scroller';
import { BREADCRUMB_SPACING, STATIC_STYLES, useDynamicStyles } from './Breadcrumb.styles';

export interface BreadcrumbProps extends ScrollerProps {
    /**
     * The child `BreadcrumbItem` components.
     */
    children: React.ReactNode;

    /**
     * The color of the link text and separator.
     * @default text.body
     */
    color?: TextColorOption;
}

/**
 * Breadcrumb is used to show the current location within a site hierarchy and allow navigation back to previous pages.
 * Structured data markup is applied to improve SEO.
 */

export function Breadcrumb(props: Readonly<BreadcrumbProps>): React.JSX.Element {
    const { children, color = 'text.body', ScrollerProps: ScrollerPropsProp, ref, ...other } = props;

    const cssVariables = useDynamicStyles(color);

    const items = React.Children.map(children, (item, index) => {
        const last = index === React.Children.count(children) - 1;
        return (
            React.isValidElement(item) &&
            item.type === BreadcrumbItem &&
            React.cloneElement(item as React.ReactElement<BreadcrumbItemProps>, {
                bcId: index === 0 ? 'bc-root' : `bc-${index - 1}`,
                bcRef: !last ? `bc-${index}` : undefined,
                last,
                position: `${index + 1}`,
            })
        );
    });

    return (
        <Scroller
            ref={ref}
            css={STATIC_STYLES}
            style={cssVariables}
            component="nav"
            aria-label="Breadcrumbs"
            columnSpacing={BREADCRUMB_SPACING}
            rowSpacing={0}
            TrackProps={{
                itemScope: true,
                itemType: 'https://schema.org/BreadcrumbList',
            }}
            {...other}
        >
            {items}
        </Scroller>
    );
}
